import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { TransactionService } from '@core/services/transaction.service';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { ApiResponse } from '@core/types';
import {
  Transaction,
  TransactionDetail,
  TransactionCreate,
  Reconciliation,
} from '@core/types/finance.type';
import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { lastValueFrom } from 'rxjs';
import { profileStore } from '@core/store';

@Injectable({ providedIn: 'root' })
export class TransactionQuery {
  private readonly transactionService = inject(TransactionService);
  private readonly queryClient = inject(QueryClient);
  private readonly profileStore = inject(profileStore);

  useTransactionsGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      const currentConfig = config();
      return {
        queryKey: [...QUERY_KEYS.transactions.grid(currentConfig), condominiumId],
        queryFn: () => lastValueFrom(this.transactionService.getTransactions(currentConfig)),
        select: (data: ApiResponse<PaginationResult<Transaction>>) => data.data,
        staleTime: environment.staleTime,
        enabled: !!condominiumId,
      };
    });
  }

  useTransactionDetail(transactionId: () => string | null) {
    return injectQuery(() => {
      const id = transactionId();
      return {
        queryKey: QUERY_KEYS.transactions.detail(id || ''),
        queryFn: () => lastValueFrom(this.transactionService.getTransaction(id!)),
        select: (data: ApiResponse<TransactionDetail>) => data.data,
        enabled: !!id,
      };
    });
  }

  useCreateManualTransaction() {
    return injectMutation(() => ({
      mutationFn: (data: TransactionCreate) =>
        lastValueFrom(this.transactionService.createManualTransaction(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.transactions.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useReconcileTransactions() {
    return injectMutation(() => ({
      mutationFn: (data: Reconciliation) =>
        lastValueFrom(this.transactionService.reconcileTransactions(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.transactions.all });
      },
    }));
  }
}
